import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Button, Label, Card, CardBody, Spinner } from 'reactstrap'
import PlacesAutocomplete from '../shared/PlacesAutoComplete'
import { Checkbox } from 'primereact/checkbox'
import PaymentModal from '../shared/PaymentModal'
import BankInfoModal from '../shared/BankInfoModal'
import server from '../../server'
import DeleteModal from '../shared/DeleteModal'
import { Button as PrimeButton } from 'primereact/button'
import { Message } from 'primereact/message'

const EditUserProfileInfo = ({
  user,
  isAdmin,
  handleEditSubmit,
  selectedShippingAddress,
  setSelectedShippingAddress,
  setEditMode,
  setUserLoading,
  onProfileUpdate,
  paymentProfileCallback,
  bankProfileCallback,
}) => {
  const [isOwnerContactDifferent, setIsOwnerContactDifferent] = useState(
    user?.pharmacyOwnerEmail || user?.pharmacyOwnerTelephone ? false : true
  )
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [bankInfoModalVisible, setBankInfoModalVisible] = useState(false)
  const [paymentProfile, setPaymentProfile] = useState(null)
  const [bankProfile, setBankProfile] = useState(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [deleteBankModalVisible, setDeleteBankModalVisible] = useState(false)
  // const [confirmDeleteBankProfile, setConfirmDeleteBankProfile] = useState(false)
  const [profileToDelete, setProfileToDelete] = useState(null)
  const [bankProfileToDelete, setBankProfileToDelete] = useState(null)
  const [paymentProfileLoading, setPaymentProfileLoading] = useState(false)
  const [bankProfileLoading, setBankProfileLoading] = useState(false)
  const module = localStorage.getItem('module')
  const handleOwnerContactChange = (e) => {
    setIsOwnerContactDifferent(e.checked)
    if (e.checked) {
      formik.setFieldValue('pharmacyOwnerTelephone', '')
      formik.setFieldValue('pharmacyOwnerEmail', '')
    } else {
      formik.setFieldValue(
        'pharmacyOwnerTelephone',
        user?.pharmacyOwnerTelephone
      )
      formik.setFieldValue('pharmacyOwnerEmail', user?.pharmacyOwnerEmail)
    }
  }

  useEffect(() => {
    // Function to fetch profile based on current module
    const fetchProfileForCurrentModule = () => {
      if (user?.pharmacyModules?.length > 0) {
        const currentModule = localStorage.getItem('module')
        const selectedModule = user.pharmacyModules.find(
          (module) => module.name === currentModule
        )
        const pharmacyId =
          selectedModule?.pharmacyId || user.pharmacyModules[0]?.pharmacyId

        if (pharmacyId) {
          fetchPaymentProfile(pharmacyId)
          fetchBankProfile(pharmacyId)
        }
      }
    }

    // Initial fetch
    fetchProfileForCurrentModule()

    // Set up listener for module changes in localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'module') {
        fetchProfileForCurrentModule()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Clean up
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [user?.pharmacyModules])

  const fetchPaymentProfile = async (pharmacyId) => {
    setPaymentProfileLoading(true)
    try {
      const response = await server.get(
        `/payments/pharmacy-payment-profile/${pharmacyId}`,
        {
        }
      )
      console.log(response?.data?.paymentProfile?.payment?.creditCard)

      // Store payment profile data
      const profileData =
        response?.data?.paymentProfile?.payment?.creditCard?.cardNumber.replace(
          'XXXX',
          ' ****'
        )

      setPaymentProfile(profileData)

      // Share the payment profile with parent component
      if (paymentProfileCallback && response?.data?.paymentProfile) {
        paymentProfileCallback(response?.data?.paymentProfile)
      }
    } catch (error) {
      console.error('Error fetching payment profile:', error)
    } finally {
      setPaymentProfileLoading(false)
    }
  }

  const fetchBankProfile = async (pharmacyId) => {
    setBankProfileLoading(true)
    try {
      const response = await server.get(
        `/payments/pharmacy-bank-profile/${pharmacyId}`,
        {
        }
      )
      console.log(response?.data)
      setBankProfile(response?.data?.bankProfile)

      // Share the bank profile with parent component
      if (bankProfileCallback && response?.data?.bankProfile) {
        bankProfileCallback(response?.data?.bankProfile)
      }

      console.log('bankProfile', bankProfile)
    } catch (error) {
      console.error('Error fetching bank profile:', error)
    } finally {
      setBankProfileLoading(false)
    }
  }

  // Yup Validation Schema
  const validationSchema = Yup.object({
    ...(user?.role !== 'pharmacy' && {
      fullName: Yup.string().required('Full Name is required'),
    }),
    ...(user?.role === 'pharmacy' && {
      legalName: Yup.string().required('Legal Name is required'),
      tradeName: Yup.string().required('Trade Name is required'),
      accreditation: Yup.number(),
      pharmacyOwnerName: Yup.string().required('Owner Name is required'),
      shippingAddress: Yup.string().required('ShippingAddress is required'),

      ...(!isOwnerContactDifferent && {
        pharmacyOwnerTelephone: Yup.string().required(
          'Contact Person Telephone is required'
        ),
        pharmacyOwnerEmail: Yup.string()
          .email('Invalid email format')
          .required('Contact Person Email is required'),
      }),
      telephone: Yup.string().required('Telephone is required'),
      fax: Yup.string().required('Fax is required'),
    }),
  })

  // Formik configuration
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: user?.displayName || '',
      legalName: user.legalName || '',
      tradeName: user?.tradeName || '',
      accreditation: user?.accreditation || '',
      pharmacyOwnerName: user?.pharmacyOwnerName || '',
      pharmacyOwnerTelephone: user?.pharmacyOwnerTelephone || '',
      pharmacyOwnerEmail: user?.pharmacyOwnerEmail || '',
      telephone: user?.telephone || '',
      fax: user?.fax || '',
      shippingAddress: user?.shippingAddress || '',
      selectedAddress: null,
    },
    validationSchema,
    onSubmit: (values) => {
      onProfileUpdate(
        values.fullName,
        values.legalName,
        values.tradeName,
        values.accreditation,
        values.shippingAddress,
        values.telephone,
        values.fax,
        values.shippingAddress,
        values.pharmacyOwnerName,
        values.pharmacyOwnerTelephone,
        values.pharmacyOwnerEmail
      )

      // Update the UI state
      setEditMode(false)
      setUserLoading(true)
    },
  })

  const handlePlaceSelect = (place) => {
    formik.setFieldValue('shippingAddress', place.formatted_address)
    setSelectedShippingAddress({
      formattedAddress: place.formatted_address,
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    })
  }

  const handleDeleteClick = (pharmacyId) => {
    setProfileToDelete(pharmacyId)
    setDeleteModalVisible(true)
  }

  const confirmDeleteProfile = async () => {
    if (profileToDelete) {
      try {
        await server.delete(
          `/payments/pharmacy-payment-profile/${profileToDelete}`,
          {
          }
        )
        setPaymentProfile(null)
        setDeleteModalVisible(false)
      } catch (error) {
        console.error('Error deleting payment profile:', error)
      }
    }
  }

  const handleDeleteBankClick = (pharmacyId) => {
    setBankProfileToDelete(pharmacyId)
    setDeleteBankModalVisible(true)
  }

  const confirmDeleteBankProfile = async () => {
    if (bankProfileToDelete) {
      try {
        await server.delete(
          `/payments/pharmacy-bank-profile/${bankProfileToDelete}`,
          {
          }
        )
        setBankProfile(null)
        setDeleteBankModalVisible(false)
      } catch (error) {
        console.error('Error deleting bank profile:', error)
      }
    }
  }

  return (
    <div>
      <h4 className='card-title mb-4'>Edit Profile</h4>
      <Card>
        <CardBody>
          <form onSubmit={formik.handleSubmit}>
            {!isAdmin && user?.role !== 'manufacturer' && (
              <h4 style={{ color: '#626ED4' }} className='mt-2 mb-2'>
                Pharmacy Information
              </h4>
            )}
            <div className='row row-cols-1 row-cols-sm-2'>
              {user?.role !== 'pharmacy' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>
                    Full Name <span className='text-danger'>*</span>
                  </Label>
                  <input
                    name='fullName'
                    className={`form-control ${
                      !formik.values.fullName ? 'border-danger' : ''
                    }`}
                    type='text'
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.fullName && formik.errors.fullName && (
                    <small className='text-danger'>
                      {formik.errors.fullName}
                    </small>
                  )}
                </div>
              )}
              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>
                    Legal Name <span className='text-danger'>*</span>
                  </Label>
                  <input
                    name='legalName'
                    className={`form-control ${
                      !formik.values.legalName ? 'border-danger' : ''
                    }`}
                    type='text'
                    value={formik.values.legalName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.legalName && formik.errors.legalName && (
                    <small className='text-danger'>
                      {formik.errors.legalName}
                    </small>
                  )}
                </div>
              )}
              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>
                    Trade Name <span className='text-danger'>*</span>
                  </Label>
                  <input
                    name='tradeName'
                    className={`form-control ${
                      !formik.values.tradeName ? 'border-danger' : ''
                    }`}
                    type='text'
                    value={formik.values.tradeName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.tradeName && formik.errors.tradeName && (
                    <small className='text-danger'>
                      {formik.errors.tradeName}
                    </small>
                  )}
                </div>
              )}
              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>Accreditation</Label>
                  <input
                    name='accreditation'
                    className='form-control'
                    type='number'
                    value={formik.values.accreditation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.accreditation &&
                    formik.errors.accreditation && (
                      <small className='text-danger'>
                        {formik.errors.accreditation}
                      </small>
                    )}
                </div>
              )}
              <div className='form-group col'>
                <Label className='form-label mt-3'>Email</Label>
                <input
                  name='email'
                  className='form-control'
                  type='text'
                  value={user.email}
                  disabled
                />
              </div>

              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>Address</Label>
                  <input
                    name='tradeName'
                    className='form-control'
                    type='text'
                    placeholder={user.address}
                    disabled={true}
                  />
                </div>
              )}
              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>
                    Shipping Address <span className='text-danger'>*</span>
                  </Label>
                  <PlacesAutocomplete
                    value={formik.values.shippingAddress}
                    placeholder={selectedShippingAddress}
                    setSelectedAddress={(e) => setSelectedShippingAddress(e)}
                    className={
                      !formik.values.shippingAddress ? 'border-danger' : ''
                    }
                  />
                </div>
              )}
              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>
                    Telephone <span className='text-danger'>*</span>
                  </Label>
                  <input
                    name='telephone'
                    className={`form-control ${
                      !formik.values.telephone ? 'border-danger' : ''
                    }`}
                    type='text'
                    value={formik.values.telephone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.telephone && formik.errors.telephone && (
                    <small className='text-danger'>
                      {formik.errors.telephone}
                    </small>
                  )}
                </div>
              )}
              {!isAdmin && user?.role !== 'manufacturer' && (
                <div className='form-group col'>
                  <Label className='form-label mt-3'>
                    Fax <span className='text-danger'>*</span>
                  </Label>
                  <input
                    name='fax'
                    className={`form-control ${
                      !formik.values.fax ? 'border-danger' : ''
                    }`}
                    type='text'
                    value={formik.values.fax}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.fax && formik.errors.fax && (
                    <small className='text-danger'>{formik.errors.fax}</small>
                  )}
                </div>
              )}
            </div>
            {!isAdmin && user?.role !== 'manufacturer' && (
              <>
                <h4 style={{ color: '#626ED4' }} className='mt-4 mb-2'>
                  Contact Person / Pharmacy Owner Information
                </h4>
                <div className='row'>
                  <div className='form-group col'>
                    <Label className='form-label mt-3'>
                      Name <span className='text-danger'>*</span>
                    </Label>
                    <input
                      name='pharmacyOwnerName'
                      className={`form-control ${
                        !formik.values.pharmacyOwnerName ? 'border-danger' : ''
                      }`}
                      type='text'
                      value={formik.values.pharmacyOwnerName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.pharmacyOwnerName &&
                      formik.errors.pharmacyOwnerName && (
                        <small className='text-danger'>
                          {formik.errors.pharmacyOwnerName}
                        </small>
                      )}
                  </div>

                  <div className='row'>
                    <div className='form-group col'>
                      <div className='d-flex align-items-center mt-4 mb-1'>
                        <Checkbox
                          inputId='originsDirect'
                          name='Origins Direct'
                          value='Origins Direct'
                          // onChange={(e) =>
                          //   setIsPharmacistInfoDifferent(e.checked)
                          // }
                          // checked={isPharmacistInfoDifferent}
                          checked={isOwnerContactDifferent}
                          onChange={handleOwnerContactChange}
                        />
                        <label htmlFor='originsDirect' className='mx-2 mb-0'>
                          Phone and Email are the same
                        </label>
                      </div>
                    </div>
                  </div>

                  {!isOwnerContactDifferent && (
                    <>
                      <div className='form-group col'>
                        <Label className='form-label mt-3'>
                          Telephone <span className='text-danger'>*</span>
                        </Label>
                        <input
                          name='pharmacyOwnerTelephone'
                          className={`form-control ${
                            !formik.values.pharmacyOwnerTelephone
                              ? 'border-danger'
                              : ''
                          }`}
                          type='text'
                          value={formik.values.pharmacyOwnerTelephone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.pharmacyOwnerTelephone &&
                          formik.errors.pharmacyOwnerTelephone && (
                            <small className='text-danger'>
                              {formik.errors.pharmacyOwnerTelephone}
                            </small>
                          )}
                      </div>
                      <div className='form-group col'>
                        <Label className='form-label mt-3'>
                          Email <span className='text-danger'>*</span>
                        </Label>
                        <input
                          name='pharmacyOwnerEmail'
                          className={`form-control ${
                            !formik.values.pharmacyOwnerEmail
                              ? 'border-danger'
                              : ''
                          }`}
                          type='text'
                          value={formik.values.pharmacyOwnerEmail}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.pharmacyOwnerEmail &&
                          formik.errors.pharmacyOwnerEmail && (
                            <small className='text-danger'>
                              {formik.errors.pharmacyOwnerEmail}
                            </small>
                          )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            {user?.role === 'pharmacy' && (
              <>
                {/* Payment Methods Section */}
                <h4 style={{ color: '#626ED4' }} className='mt-4 mb-2'>
                  Payment and Banking Information
                </h4>

                <div className='row mt-3'>
                  {module === 'Origins Direct Instant' &&
                    (paymentProfile === null || bankProfile === null) && (
                      <div className='mb-4'>
                        <Message
                          severity='warn'
                          text='In order to have access to the Origins Direct Instant platform for payments and fund transfers, please fill this information.'
                        />
                      </div>
                    )}
                  <div className='col-md-6'>
                    <div className='card border h-100'>
                      {paymentProfileLoading ? (
                        <div className='card-body d-flex flex-column align-items-center justify-content-center p-4'>
                          <Spinner color='primary' />
                          <p className='mt-2'>Loading payment information...</p>
                        </div>
                      ) : !paymentProfile ? (
                        <div className='card-body d-flex flex-column align-items-center justify-content-center p-4'>
                          <h5 className='mb-4'>Add a Payment Method</h5>
                          <Button
                            color=''
                            className='btn btn-outline-primary'
                            onClick={() => setPaymentModalVisible(true)}
                          >
                            <i className='mdi mdi-plus-circle-outline me-1'></i>{' '}
                            Add a Card
                          </Button>
                        </div>
                      ) : (
                        <div className='card-body p-4 d-flex flex-column justify-content-between'>
                          <div>
                            <p className='mb-0 fw-bold'>
                              <i className='mdi mdi-credit-card me-2'></i>
                              Visa {paymentProfile}
                            </p>
                            {/* <div className='d-flex align-items-center mt-1'>
                              <span>Expires {paymentProfile?.expiryDate}</span>
                              <a
                                href='#'
                                className=' ms-2'
                                style={{ color: '#626ED4' }}
                              >
                                Update
                              </a>
                            </div> */}
                          </div>

                          <div className='mt-4 d-flex gap-2'>
                            {/* <Button color='primary' className='btn'>
                          Charge
                        </Button>
                        <Button color='' className='btn btn-outline-primary'>
                          Edit
                        </Button> */}
                            <Button
                              color=''
                              className='btn btn-outline-primary'
                              onClick={() => {
                                const pharmacyId =
                                  user?.pharmacyModules?.[0]?.pharmacyId
                                if (pharmacyId) {
                                  handleDeleteClick(pharmacyId)
                                }
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <div className='card border h-100'>
                      {bankProfileLoading ? (
                        <div className='card-body d-flex flex-column align-items-center justify-content-center p-4'>
                          <Spinner color='primary' />
                          <p className='mt-2'>Loading bank information...</p>
                        </div>
                      ) : !bankProfile ? (
                        <div className='card-body d-flex flex-column align-items-center justify-content-center p-4'>
                          <h5 className='mb-4'>Add a Bank Information</h5>
                          <Button
                            color=''
                            className='btn btn-outline-primary'
                            onClick={() => setBankInfoModalVisible(true)}
                          >
                            <i className='mdi mdi-plus-circle-outline me-1'></i>{' '}
                            Add a Bank Information
                          </Button>
                        </div>
                      ) : (
                        <div className='card-body p-4 d-flex flex-column justify-content-between'>
                          <div>
                            <p className='mb-0 fw-bold'>
                              <i className='mdi mdi-bank me-2'></i>
                              Account:{' '}
                              {bankProfile?.payment?.bankAccount?.accountNumber.replace(
                                'XXXX',
                                ' ****'
                              )}
                            </p>
                            <div className='d-flex align-items-center mt-1'>
                              <span className='me-2 fw-bold'>
                                Account Holder Name:{' '}
                                {
                                  bankProfile?.payment?.bankAccount
                                    ?.nameOnAccount
                                }
                              </span>
                            </div>
                          </div>

                          <div className='mt-4 d-flex gap-2'>
                            <Button
                              color=''
                              className='btn btn-outline-primary'
                              onClick={() => {
                                const pharmacyId =
                                  user?.pharmacyModules?.[0]?.pharmacyId
                                if (pharmacyId) {
                                  handleDeleteBankClick(pharmacyId)
                                }
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className='text-center mt-4 d-flex justify-content-end gap-3 col-12'>
              <Button
                color=''
                className='btn btn-outline-primary'
                onClick={() => setEditMode(false)}
              >
                Cancel
              </Button>
              <Button type='submit' color='primary'>
                Save
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
      <PaymentModal
        visible={paymentModalVisible}
        setVisible={setPaymentModalVisible}
        onSave={(cardData) => {
          // Here you would typically make an API call to save the card
          console.log('Card saved for pharmacy:', cardData.pharmacyId)
          console.log('Card data:', cardData)

          // After successful payment processing, refresh the payment profile data
          if (cardData.pharmacyId) {
            fetchPaymentProfile(cardData.pharmacyId)
          }
        }}
        apiLoginID={process.env.REACT_APP_AUTHORIZE_API_LOGIN_ID}
        clientKey={process.env.REACT_APP_AUTHORIZE_CLIENT_KEY}
        pharmacyId={user?.pharmacyModules?.[0]?.pharmacyId || ''}
        userId={user?.id || ''}
      />
      <BankInfoModal
        visible={bankInfoModalVisible}
        setVisible={setBankInfoModalVisible}
        onSave={(bankData) => {
          // Here you would typically make an API call to save the bank info
          console.log('Bank info saved for pharmacy:', bankData.pharmacyId)
          console.log('Bank data:', bankData)
          setBankProfile(bankData)

          // After successful bank info processing, refresh the bank profile data
          if (bankData.pharmacyId) {
            fetchBankProfile(bankData.pharmacyId)
          }
        }}
        apiLoginID={process.env.REACT_APP_AUTHORIZE_API_LOGIN_ID}
        clientKey={process.env.REACT_APP_AUTHORIZE_CLIENT_KEY}
        pharmacyId={user?.pharmacyModules?.[0]?.pharmacyId || ''}
        userId={user?.id || ''}
      />
      <DeleteModal
        state={deleteBankModalVisible}
        footer={
          <React.Fragment>
            <PrimeButton
              label='No'
              icon='pi pi-times'
              outlined
              onClick={() => setDeleteBankModalVisible(false)}
            />
            <PrimeButton
              label='Yes'
              icon='pi pi-check'
              severity='primary'
              onClick={confirmDeleteBankProfile}
            />
          </React.Fragment>
        }
        onHide={() => setDeleteBankModalVisible(false)}
        deletedItem={user?.pharmacyModules?.[0]?.pharmacyId || ''}
        name={bankProfile?.payment?.bankAccount?.accountNumber || ''}
      />
      <DeleteModal
        state={deleteModalVisible}
        footer={
          <React.Fragment>
            <PrimeButton
              label='No'
              icon='pi pi-times'
              outlined
              onClick={() => setDeleteModalVisible(false)}
            />
            <PrimeButton
              label='Yes'
              icon='pi pi-check'
              severity='primary'
              onClick={confirmDeleteProfile}
            />
          </React.Fragment>
        }
        onHide={() => setDeleteModalVisible(false)}
        deletedItem={user?.pharmacyModules?.[0]?.pharmacyId || ''}
        name={paymentProfile || 'this payment method'}
      />
    </div>
  )
}

export default EditUserProfileInfo
